import classNames from "classnames";
import {isEmpty} from "lodash";
import {type FC, Fragment, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";

import {ArrowCircleUpIcon, ExternalLinkIcon, LogoIcon} from "assets/icons";
import {Button} from "components";
import {useAppDispatch, useAppSelector, useOutsideClick} from "hooks";
import {useAccessControl} from "mapx-components/Layouts/Sidebar/useAccessControl";
import {usePolarisAccess} from "mapx-pages/Polaris/usePolarisAccess";
import type {TWatchVideoHandle} from "mapx-components/WatchVideo/types";
import {contactAdminModal, pricingTableModal} from "store/payment/paymentActions";
import {projectSelector, projectsSelector} from "store/project-list/projectListSelectors";
import {onboardingForStarterUserSelector} from "store/project/projectSelectors";
import {
	hasAccessToExtendedPagesSelector,
	isSuperUserSelector,
	userLicenseTypeNameSelector,
	userOrganisationRoleSelector,
	userOrganisationTypeSelector,
	userSelector,
} from "store/user/userSelectors";

import LogoIconSmall from "assets/icons/LogoIconSmall";
import UserImageOrAvatar from "assets/icons/userImageOrAvatar";
import TwoFactorAuthModal from "mapx-components/Modals/TwoFactorAuthModal";
import {CgClose, CgMenu} from "react-icons/cg";
import {setSideBarExtended} from "store/layout/layoutActions";
import {isSidebarExpandedSelector} from "store/layout/layoutSelectors";
import {organisationHasPaidPlanSelector} from "store/organisation/organisationSelectors";
import packageJson from "../../../../package.json";
import {ChangePasswordModal} from "../../index";
import HeadUser from "../Header/HeadUser";
import AnonymiseProfiles from "./AnonymiseProfiles";
import NavItem from "./NavItem";
import styles from "./Sidebar.module.scss";
import type {TNavItemProps} from "./types";
import {pages} from "./utils";

const Sidebar: FC = () => {
	// const history = useHistory();
	const location = useLocation();
	const dispatch = useAppDispatch();

	const ref = useRef<HTMLDivElement>(null);
	const watchVideoRef = useRef<TWatchVideoHandle | null>(null);
	const isHome = location.pathname === "/home";

	const [expanded, setExpanded] = useState<boolean>(false);

	const [[changePassModalDisplay], setChangePassModalDisplay] = useState<[boolean, null]>([
		false,
		null,
	]);

	const [twoFactorAuthModalDisplay, setTwoFactorAuthModalDisplay] = useState<boolean>(false);

	const user = useAppSelector(userSelector);
	const project = useAppSelector(projectSelector);
	const projects = useAppSelector(projectsSelector);
	const isSuperUser = useAppSelector(isSuperUserSelector);
	const organisationType = useAppSelector(userOrganisationTypeSelector);

	const organisationRole = useAppSelector(userOrganisationRoleSelector);
	const userLicenseTypeName = useAppSelector(userLicenseTypeNameSelector);
	const organisationHasPaidPlan = useAppSelector(organisationHasPaidPlanSelector);

	// const shouldDisplayOnBoarding =
	// 	userLicenseTypeName === "Starter" || userLicenseTypeName === "Plus";

	const isAdmin = organisationRole === "Admin";

	const isSidebarExpanded = useAppSelector(isSidebarExpandedSelector);

	const onboardingForStarterUser = useAppSelector(onboardingForStarterUserSelector);

	const hasAccessToExtendedPages = useAppSelector(hasAccessToExtendedPagesSelector);

	const hoverTimer = useRef<NodeJS.Timeout | null>(null);

	useAccessControl(hasAccessToExtendedPages);

	useOutsideClick(ref, () => {
		setTimeout(() => {
			setChangePassModalDisplay([false, null]);
			if (isSidebarExpanded) {
				setExpanded(false);
				dispatch(setSideBarExtended(false));
			}
		}, 400);
	});

	const openPaymentModalHandler = () => {
		if (organisationHasPaidPlan) {
			dispatch(contactAdminModal(true));
		} else {
			dispatch(pricingTableModal(true));
		}
	};

	useEffect(() => {
		dispatch(setSideBarExtended(expanded));
	}, [expanded, dispatch]);

	const handleMouseLeave = () => {
		if (hoverTimer.current) {
			setExpanded(false);
			clearTimeout(hoverTimer.current);
			hoverTimer.current = null;
		}
	};
	const handleMouseEnter = () => {
		hoverTimer.current = setTimeout(() => {
			if (!watchVideoRef.current?.show) {
				setExpanded(true);
			}
		}, 500);
	};

	useEffect(() => {
		return () => {
			if (hoverTimer.current) {
				clearTimeout(hoverTimer.current);
				hoverTimer.current = null;
			}
		};
	}, []);

	const notClient: boolean = organisationType !== "Client";
	const polarisEnabled = usePolarisAccess();
	const superUserOrNotClient = isSuperUser && notClient;

	const renderPageSteps = pages(
		isSuperUser,
		superUserOrNotClient,
		hasAccessToExtendedPages,
		isEmpty(projects),
		isAdmin,
		onboardingForStarterUser,
		notClient,
		polarisEnabled === true,
	)?.map((item: TNavItemProps, idx) => {
		if (item.Icon) {
			return (
				<div key={`${item.name}-${idx}`} className={styles.navItemWrapper}>
					<NavItem
						name={item.name}
						Icon={item.Icon}
						path={item.path}
						collapsed={!expanded}
						childPaths={item.childPaths}
					/>
				</div>
			);
		}

		return null;
	});

	const renderNavClassName = classNames(styles.nav, {
		[styles.is_home]: isHome,
		[styles.navCollapsed]: !expanded,
		[styles.nav__selected]: project?.name,
		[styles.is_z_index]: onboardingForStarterUser,
	});
	const WEB_TUTORIAL_URL = process.env?.REACT_APP_DWS_WEB_TUTORIAL_URL;

	return (
		<div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
			{!expanded && (
				<CgMenu
					className={styles.menu}
					onClick={() => setExpanded(!expanded)}
					size={25}
					color={"#808080"}
				/>
			)}

			{expanded && (
				<CgClose
					className={classNames(styles.menu, styles.closeMenu)}
					onClick={() => setExpanded(!expanded)}
					size={20}
					color={"#808080"}
				/>
			)}

			<nav ref={ref} className={renderNavClassName} data-testid="nav-sidebar-container">
				<Button defaultBtnStyle className={styles.navItems__logo} to="/home">
					{expanded ? (
						<span style={{marginLeft: "60px"}} className={styles.logoFadeIn}>
							<LogoIcon />
						</span>
					) : (
						<LogoIconSmall className={styles.logoFadeIn} />
					)}
				</Button>

				<div className={styles.navItems}>
					<div className={styles.navItems__container}>{renderPageSteps}</div>

					<div className={styles.navItems__footer}>
						{isSuperUser && <AnonymiseProfiles expanded={expanded} />}

						{WEB_TUTORIAL_URL && (
							<Fragment>
								<div
									role="button"
									className={classNames(styles.navItems__footer__info, {
										[styles.navItems__footer__info_close]: !expanded,
									})}
									onClick={() => window.open(WEB_TUTORIAL_URL, "_blank")}
								>
									<ExternalLinkIcon color="#868d8d" />

									{expanded && (
										<p className={styles.navItems__footer__info_text}>
											Help Center
										</p>
									)}
								</div>

								<div className={styles.navItems__footer__solid} />
							</Fragment>
						)}

						<div
							className={classNames(styles.navItems__footer__user, {
								[styles.navItems__footer__user_close]: !expanded,
							})}
						>
							{!expanded ? (
								<UserImageOrAvatar />
							) : (
								<div className={styles.navItems__footer__user__info}>
									<HeadUser
										setModalDisplay={setChangePassModalDisplay}
										setTwoFactorAuthModalDisplay={setTwoFactorAuthModalDisplay}
									/>
								</div>
							)}

							<ChangePasswordModal
								ref={ref}
								changePassModalDisplay={changePassModalDisplay}
								setChangePassModalDisplay={setChangePassModalDisplay}
							/>

							<TwoFactorAuthModal
								twoFactorAuthModalDisplay={twoFactorAuthModalDisplay}
								setTwoFactorAuthModalDisplay={setTwoFactorAuthModalDisplay}
							/>
						</div>

						{(userLicenseTypeName === "Plus" || (isAdmin && organisationHasPaidPlan)) &&
							expanded && (
								<div className={styles.navItems__footer__plus}>
									<p className={styles.navItems__footer__plus_title}>
										Want even more features?
									</p>

									<a
										target="blank"
										className={styles.navItems__footer__plus_description}
										href={`https://share-eu1.hsforms.com/1E6nq5jD6Qyei811lydzk4wfkdks?email=${user.email}&firstname=${user.firstName}&lastname=${user.lastName}&company=${user.organisation.name}`}
									>
										Inquire about our Pro and <br /> Enterprise licenses
									</a>
								</div>
							)}

						{/*{(userLicenseTypeName === "Enterprise" || userLicenseTypeName === "Pro") && (*/}
						{/*	<WatchVideo*/}
						{/*		expanded={expanded}*/}
						{/*		ref={watchVideoRef}*/}
						{/*		className={styles.navItems__footer_video}*/}
						{/*	/>*/}
						{/*)}*/}

						{userLicenseTypeName === "Starter" &&
							!(isAdmin && organisationHasPaidPlan) && (
								<Button
									LeftIcon={ArrowCircleUpIcon}
									onClick={openPaymentModalHandler}
									className={classNames(styles.navItems__upgrade, {
										[styles.navItems__upgrade_expanded]: expanded,
									})}
									leftIconClasses={classNames(styles.navItems__upgrade__icon, {
										[styles.navItems__upgrade__icon_expanded]: expanded,
									})}
								>
									{expanded && (
										<div className={styles.navItems__upgrade__content}>
											<p className={styles.navItems__upgrade__content_title}>
												Upgrade to
												<span className={styles.plus}>PLUS</span>
											</p>
											<span
												className={
													styles.navItems__upgrade__content_subtitle
												}
											>
												7 days free trial
											</span>
										</div>
									)}
								</Button>
							)}
						{expanded && (
							<p className={styles.navItems__footer__version}>
								v{packageJson.version}
							</p>
						)}
					</div>
				</div>
			</nav>
		</div>
	);
};

export default Sidebar;
